import { DeferredOverlay } from '../../components/DeferredOverlay';
import { Icon } from '../../components/Icon';
import { RAIL_ICON, RAIL_ITEM_IDLE_TEXT, RAIL_ITEM_OPEN_TEXT } from '../../components/railItem';
import { CountBadge } from '../inbox/UnreadMail';
import { alertBadgeTone, useRailBadges } from './useRailBadges';

/**
 * The notifications panel — the bell in the rail, and what is behind it.
 *
 * ── WHAT LANDS IN IT ─────────────────────────────────────────────────────────────────────────
 *
 * Two audiences in one list: what the AGENCY needs to know (an automation stopped itself, a
 * message could not be delivered, work was raised onto a queue) and what YOU need to know (your
 * access changed). They are not separated into tabs, because a person opening this is asking one
 * question — "what have I missed" — and answering it with a chooser makes them ask it twice. The
 * personal ones are marked instead.
 *
 * It is NOT the mailer's queue. `notifications` in the database is a message owed to an owner or a
 * tenant and sent out of the building; this never leaves the browser. See `@promanage/core/alerts`
 * for why the two must not share a name.
 *
 * ── ONE QUERY, MOUNTED BY THE RAIL, SHARED BY BOTH SURFACES ──────────────────────────────────
 *
 * The rail is on every page, so this is the most-called read in the product by construction. It is
 * one request that returns the list AND the unread count — the trigger needs the count whether or
 * not anybody opens the panel, and a second endpoint for it would be a second thing that can
 * disagree with what the panel then shows. The phone's sheet reads the same cached query.
 *
 * ── READ STATE IS THE SERVER'S, PER PERSON ───────────────────────────────────────────────────
 *
 * Nothing here is stored in the browser. An agency-wide alert read by one manager stays unread for
 * the next — that is the whole reason `alert_reads` is its own table — and a `localStorage` copy
 * would make "have I seen this" depend on which machine somebody is sitting at.
 */

/**
 * The rail's bell — an icon, not a row.
 *
 * It sits beside the account button rather than under a label of its own: the rail's height goes
 * to places, and a bell needs no word to say what it is. So it is a square at the rail's row height
 * in both states, and the unread COUNT rides on the glyph as a badge — "3" is a different fact from
 * "there is something", and with no row to hold a count beside a label, the icon is where it goes.
 */
export function NotificationsRailItem() {
  const badges = useRailBadges();
  const unread = badges?.alerts.unread ?? 0;

  return (
    <DeferredOverlay
      overlay="NotificationsPopover"
      failureTitle="Could not open your notifications"
      triggerProps={{
        /*
         * The unread state belongs in the accessible NAME, and as a number rather than as the word
         * "unread". A label wins over contents, so an `sr-only` span inside this button would be
         * announced to nobody — and "Notifications (unread)" told a screen reader user less than
         * the badge tells everybody else.
         */
        'aria-label': unread === 0 ? 'Notifications' : `Notifications (${String(unread)} unread)`,
        title: 'Notifications',
        className: `flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-md short:size-[34px] hover:bg-surface-sunk data-[state=open]:bg-surface-sunk ${RAIL_ITEM_IDLE_TEXT} ${RAIL_ITEM_OPEN_TEXT}`,
      }}
    >
      <span className="relative flex shrink-0 items-center justify-center">
        <Icon name="bell" size={RAIL_ICON} />
        {/* Red only while something urgent is unread; otherwise the blue of any other count. */}
        <CountBadge
          count={unread}
          tone={alertBadgeTone(badges?.alerts.urgent ?? 0)}
          className="absolute -top-1.5 -right-2 font-bold ring-2 ring-surface"
        />
      </span>
    </DeferredOverlay>
  );
}
